/**
 * ThemeManager.js - Centralized theme handler for DCACLab
 * 
 * This module provides a single handler to apply any theme with one click.
 * All color definitions are centralized here for easy maintenance.
 * 
 * Usage:
 *   import { applyTheme, getThemeNames, getCurrentTheme } from './themes/ThemeManager';
 *   applyTheme('espresso'); // Apply a theme by name
 */

// ============================================================================
// THEME DEFINITIONS
// ============================================================================
// Each theme is a complete set of CSS variables that will be applied to :root
// To add a new theme, simply add a new object with all the required variables

const themes = {
    // -------------------------------------------------------------------------
    // SLATE (Current Default) - Modern professional cool gray tones
    // -------------------------------------------------------------------------
    slate: {
        name: 'Slate',
        description: 'Modern and professional cool gray tones',
        isDark: false,
        colors: {
            '--background': 'oklch(0.97 0.01 260)',
            '--foreground': 'oklch(0.25 0.03 260)',
            '--card': 'oklch(0.99 0.005 260)',
            '--card-foreground': 'oklch(0.25 0.03 260)',
            '--popover': 'oklch(0.99 0.005 260)',
            '--popover-foreground': 'oklch(0.25 0.03 260)',
            '--primary': 'oklch(0.45 0.04 260)',
            '--primary-foreground': 'oklch(1.0 0 0)',
            '--secondary': 'oklch(0.92 0.02 260)',
            '--secondary-foreground': 'oklch(0.30 0.03 260)',
            '--muted': 'oklch(0.94 0.01 260)',
            '--muted-foreground': 'oklch(0.45 0.03 260)',
            '--accent': 'oklch(0.90 0.01 260)',
            '--accent-foreground': 'oklch(0.25 0.03 260)',
            '--destructive': 'oklch(0.55 0.20 25)',
            '--destructive-foreground': 'oklch(1.0 0 0)',
            '--border': 'oklch(0.88 0.02 260)',
            '--input': 'oklch(0.88 0.02 260)',
            '--ring': 'oklch(0.45 0.04 260)',
            '--chart-1': 'oklch(0.45 0.04 260)',
            '--chart-2': 'oklch(0.50 0.10 240)',
            '--chart-3': 'oklch(0.55 0.08 280)',
            '--chart-4': 'oklch(0.60 0.06 220)',
            '--chart-5': 'oklch(0.40 0.05 260)',
            '--sidebar': 'oklch(0.95 0.015 260)',
            '--sidebar-foreground': 'oklch(0.25 0.03 260)',
            '--sidebar-primary': 'oklch(0.45 0.04 260)',
            '--sidebar-primary-foreground': 'oklch(1.0 0 0)',
            '--sidebar-accent': 'oklch(0.90 0.01 260)',
            '--sidebar-accent-foreground': 'oklch(0.25 0.03 260)',
            '--sidebar-border': 'oklch(0.88 0.02 260)',
            '--sidebar-ring': 'oklch(0.45 0.04 260)',
            '--shadow-color': 'hsl(260 20% 20%)',
        },
        darkColors: {
            '--background': 'oklch(0.18 0.02 260)',
            '--foreground': 'oklch(0.92 0.01 260)',
            '--card': 'oklch(0.22 0.025 260)',
            '--card-foreground': 'oklch(0.92 0.01 260)',
            '--popover': 'oklch(0.22 0.025 260)',
            '--popover-foreground': 'oklch(0.92 0.01 260)',
            '--primary': 'oklch(0.65 0.04 260)',
            '--primary-foreground': 'oklch(0.18 0.02 260)',
            '--secondary': 'oklch(0.28 0.03 260)',
            '--secondary-foreground': 'oklch(0.92 0.01 260)',
            '--muted': 'oklch(0.24 0.02 260)',
            '--muted-foreground': 'oklch(0.70 0.02 260)',
            '--accent': 'oklch(0.30 0.02 260)',
            '--accent-foreground': 'oklch(0.92 0.01 260)',
            '--destructive': 'oklch(0.55 0.20 25)',
            '--destructive-foreground': 'oklch(1.0 0 0)',
            '--border': 'oklch(0.30 0.025 260)',
            '--input': 'oklch(0.30 0.025 260)',
            '--ring': 'oklch(0.65 0.04 260)',
            '--chart-1': 'oklch(0.65 0.04 260)',
            '--chart-2': 'oklch(0.60 0.10 240)',
            '--chart-3': 'oklch(0.70 0.08 280)',
            '--chart-4': 'oklch(0.55 0.06 220)',
            '--chart-5': 'oklch(0.75 0.05 260)',
            '--sidebar': 'oklch(0.16 0.02 260)',
            '--sidebar-foreground': 'oklch(0.92 0.01 260)',
            '--sidebar-primary': 'oklch(0.65 0.04 260)',
            '--sidebar-primary-foreground': 'oklch(0.18 0.02 260)',
            '--sidebar-accent': 'oklch(0.30 0.02 260)',
            '--sidebar-accent-foreground': 'oklch(0.92 0.01 260)',
            '--sidebar-border': 'oklch(0.30 0.025 260)',
            '--sidebar-ring': 'oklch(0.65 0.04 260)',
            '--shadow-color': 'hsl(260 20% 5%)',
        }
    },

    // -------------------------------------------------------------------------
    // ESPRESSO - Deep coffee and dark chocolate brown tones
    // -------------------------------------------------------------------------
    espresso: {
        name: 'Espresso',
        description: 'Rich coffee and dark chocolate tones',
        isDark: true,
        colors: {
            // Dark coffee bean background
            '--background': 'oklch(0.15 0.02 50)',
            '--foreground': 'oklch(0.95 0.02 60)',
            // Deep chocolate cards
            '--card': 'oklch(0.20 0.03 50)',
            '--card-foreground': 'oklch(0.95 0.02 60)',
            '--popover': 'oklch(0.20 0.03 50)',
            '--popover-foreground': 'oklch(0.95 0.02 60)',
            // Roasted espresso primary
            '--primary': 'oklch(0.65 0.12 60)',
            '--primary-foreground': 'oklch(0.15 0.02 50)',
            // Warm latte secondary
            '--secondary': 'oklch(0.25 0.04 55)',
            '--secondary-foreground': 'oklch(0.90 0.03 60)',
            // Muted steamed milk
            '--muted': 'oklch(0.22 0.02 50)',
            '--muted-foreground': 'oklch(0.75 0.03 60)',
            // Caramel accent
            '--accent': 'oklch(0.35 0.06 65)',
            '--accent-foreground': 'oklch(0.95 0.02 60)',
            '--destructive': 'oklch(0.55 0.18 30)',
            '--destructive-foreground': 'oklch(1.00 0 0)',
            // Dark cocoa borders
            '--border': 'oklch(0.28 0.03 55)',
            '--input': 'oklch(0.28 0.03 55)',
            '--ring': 'oklch(0.65 0.12 60)',
            // Bright vibrant charts for better visibility on dark background
            '--chart-1': 'oklch(0.75 0.18 55)',  // Bright Caramel/Orange
            '--chart-2': 'oklch(0.70 0.16 150)', // Vibrant Teal/Green
            '--chart-3': 'oklch(0.80 0.14 80)',  // Bright Golden Yellow
            '--chart-4': 'oklch(0.65 0.20 25)',  // Vibrant Coral/Red
            '--chart-5': 'oklch(0.72 0.15 280)', // Bright Purple/Violet
            // Sidebar - rich and dark
            '--sidebar': 'oklch(0.13 0.02 50)',
            '--sidebar-foreground': 'oklch(0.90 0.02 60)',
            '--sidebar-primary': 'oklch(0.65 0.12 60)',
            '--sidebar-primary-foreground': 'oklch(0.15 0.02 50)',
            '--sidebar-accent': 'oklch(0.35 0.06 65)',
            '--sidebar-accent-foreground': 'oklch(0.95 0.02 60)',
            '--sidebar-border': 'oklch(0.25 0.03 55)',
            '--sidebar-ring': 'oklch(0.65 0.12 60)',
            '--shadow-color': 'hsl(30 20% 5%)',
        }
    },

    // -------------------------------------------------------------------------
    // EARTHY - Warm, organic tones with brown/beige palette
    // -------------------------------------------------------------------------
    earthy: {
        name: 'Earthy',
        description: 'Warm, organic tones with a natural feel',
        isDark: false,
        colors: {
            '--background': 'oklch(0.9582 0.0152 90.2357)',
            '--foreground': 'oklch(0.3760 0.0225 64.3434)',
            '--card': 'oklch(0.9914 0.0098 87.4695)',
            '--card-foreground': 'oklch(0.3760 0.0225 64.3434)',
            '--popover': 'oklch(0.9914 0.0098 87.4695)',
            '--popover-foreground': 'oklch(0.3760 0.0225 64.3434)',
            '--primary': 'oklch(0.6180 0.0778 65.5444)',
            '--primary-foreground': 'oklch(1.0000 0 0)',
            '--secondary': 'oklch(0.8846 0.0302 85.5655)',
            '--secondary-foreground': 'oklch(0.4313 0.0300 64.9288)',
            '--muted': 'oklch(0.9239 0.0190 83.0636)',
            '--muted-foreground': 'oklch(0.5391 0.0387 71.1655)',
            '--accent': 'oklch(0.8348 0.0426 88.8064)',
            '--accent-foreground': 'oklch(0.3760 0.0225 64.3434)',
            '--destructive': 'oklch(0.5471 0.1438 32.9149)',
            '--destructive-foreground': 'oklch(1.0000 0 0)',
            '--border': 'oklch(0.8606 0.0321 84.5881)',
            '--input': 'oklch(0.8606 0.0321 84.5881)',
            '--ring': 'oklch(0.6180 0.0778 65.5444)',
            '--chart-1': 'oklch(0.6180 0.0778 65.5444)',
            '--chart-2': 'oklch(0.5604 0.0624 68.5805)',
            '--chart-3': 'oklch(0.4851 0.0570 72.6827)',
            '--chart-4': 'oklch(0.6777 0.0624 64.7755)',
            '--chart-5': 'oklch(0.7264 0.0581 66.6967)',
            '--sidebar': 'oklch(0.9239 0.0190 83.0636)',
            '--sidebar-foreground': 'oklch(0.3760 0.0225 64.3434)',
            '--sidebar-primary': 'oklch(0.6180 0.0778 65.5444)',
            '--sidebar-primary-foreground': 'oklch(1.0000 0 0)',
            '--sidebar-accent': 'oklch(0.8348 0.0426 88.8064)',
            '--sidebar-accent-foreground': 'oklch(0.3760 0.0225 64.3434)',
            '--sidebar-border': 'oklch(0.8606 0.0321 84.5881)',
            '--sidebar-ring': 'oklch(0.6180 0.0778 65.5444)',
            '--shadow-color': 'hsl(28 13% 20%)',
        },
        darkColors: {
            '--background': 'oklch(0.2747 0.0139 57.6523)',
            '--foreground': 'oklch(0.9239 0.0190 83.0636)',
            '--card': 'oklch(0.3237 0.0155 59.0603)',
            '--card-foreground': 'oklch(0.9239 0.0190 83.0636)',
            '--popover': 'oklch(0.3237 0.0155 59.0603)',
            '--popover-foreground': 'oklch(0.9239 0.0190 83.0636)',
            '--primary': 'oklch(0.7264 0.0581 66.6967)',
            '--primary-foreground': 'oklch(0.2747 0.0139 57.6523)',
            '--secondary': 'oklch(0.3795 0.0181 57.1280)',
            '--secondary-foreground': 'oklch(0.9239 0.0190 83.0636)',
            '--muted': 'oklch(0.2939 0.0125 62.1298)',
            '--muted-foreground': 'oklch(0.7982 0.0243 82.1078)',
            '--accent': 'oklch(0.4186 0.0281 56.3404)',
            '--accent-foreground': 'oklch(0.9239 0.0190 83.0636)',
            '--destructive': 'oklch(0.5471 0.1438 32.9149)',
            '--destructive-foreground': 'oklch(1.0000 0 0)',
            '--border': 'oklch(0.3795 0.0181 57.1280)',
            '--input': 'oklch(0.3795 0.0181 57.1280)',
            '--ring': 'oklch(0.7264 0.0581 66.6967)',
            '--chart-1': 'oklch(0.7264 0.0581 66.6967)',
            '--chart-2': 'oklch(0.6777 0.0624 64.7755)',
            '--chart-3': 'oklch(0.6180 0.0778 65.5444)',
            '--chart-4': 'oklch(0.5604 0.0624 68.5805)',
            '--chart-5': 'oklch(0.4851 0.0570 72.6827)',
            '--sidebar': 'oklch(0.2747 0.0139 57.6523)',
            '--sidebar-foreground': 'oklch(0.9239 0.0190 83.0636)',
            '--sidebar-primary': 'oklch(0.7264 0.0581 66.6967)',
            '--sidebar-primary-foreground': 'oklch(0.2747 0.0139 57.6523)',
            '--sidebar-accent': 'oklch(0.4186 0.0281 56.3404)',
            '--sidebar-accent-foreground': 'oklch(0.9239 0.0190 83.0636)',
            '--sidebar-border': 'oklch(0.3795 0.0181 57.1280)',
            '--sidebar-ring': 'oklch(0.7264 0.0581 66.6967)',
            '--shadow-color': 'hsl(28 13% 10%)',
        }
    },

    // -------------------------------------------------------------------------
    // OCEAN - Cool blue tones, professional and calm
    // -------------------------------------------------------------------------
    ocean: {
        name: 'Ocean',
        description: 'Cool blue tones with a professional feel',
        isDark: false,
        colors: {
            '--background': 'oklch(0.97 0.01 230)',
            '--foreground': 'oklch(0.25 0.03 240)',
            '--card': 'oklch(0.99 0.005 230)',
            '--card-foreground': 'oklch(0.25 0.03 240)',
            '--popover': 'oklch(0.99 0.005 230)',
            '--popover-foreground': 'oklch(0.25 0.03 240)',
            '--primary': 'oklch(0.55 0.15 240)',
            '--primary-foreground': 'oklch(1.0 0 0)',
            '--secondary': 'oklch(0.90 0.03 220)',
            '--secondary-foreground': 'oklch(0.35 0.04 240)',
            '--muted': 'oklch(0.93 0.015 225)',
            '--muted-foreground': 'oklch(0.50 0.04 235)',
            '--accent': 'oklch(0.85 0.05 210)',
            '--accent-foreground': 'oklch(0.25 0.03 240)',
            '--destructive': 'oklch(0.55 0.20 25)',
            '--destructive-foreground': 'oklch(1.0 0 0)',
            '--border': 'oklch(0.88 0.025 225)',
            '--input': 'oklch(0.88 0.025 225)',
            '--ring': 'oklch(0.55 0.15 240)',
            '--chart-1': 'oklch(0.55 0.15 240)',
            '--chart-2': 'oklch(0.50 0.18 200)',
            '--chart-3': 'oklch(0.60 0.12 180)',
            '--chart-4': 'oklch(0.45 0.10 260)',
            '--chart-5': 'oklch(0.65 0.08 220)',
            '--sidebar': 'oklch(0.94 0.015 225)',
            '--sidebar-foreground': 'oklch(0.25 0.03 240)',
            '--sidebar-primary': 'oklch(0.55 0.15 240)',
            '--sidebar-primary-foreground': 'oklch(1.0 0 0)',
            '--sidebar-accent': 'oklch(0.85 0.05 210)',
            '--sidebar-accent-foreground': 'oklch(0.25 0.03 240)',
            '--sidebar-border': 'oklch(0.88 0.025 225)',
            '--sidebar-ring': 'oklch(0.55 0.15 240)',
            '--shadow-color': 'hsl(220 30% 20%)',
        },
        darkColors: {
            '--background': 'oklch(0.20 0.02 240)',
            '--foreground': 'oklch(0.92 0.015 225)',
            '--card': 'oklch(0.25 0.025 238)',
            '--card-foreground': 'oklch(0.92 0.015 225)',
            '--popover': 'oklch(0.25 0.025 238)',
            '--popover-foreground': 'oklch(0.92 0.015 225)',
            '--primary': 'oklch(0.65 0.15 230)',
            '--primary-foreground': 'oklch(0.15 0.02 240)',
            '--secondary': 'oklch(0.30 0.03 235)',
            '--secondary-foreground': 'oklch(0.92 0.015 225)',
            '--muted': 'oklch(0.25 0.02 235)',
            '--muted-foreground': 'oklch(0.75 0.02 225)',
            '--accent': 'oklch(0.35 0.04 230)',
            '--accent-foreground': 'oklch(0.92 0.015 225)',
            '--destructive': 'oklch(0.55 0.20 25)',
            '--destructive-foreground': 'oklch(1.0 0 0)',
            '--border': 'oklch(0.32 0.025 235)',
            '--input': 'oklch(0.32 0.025 235)',
            '--ring': 'oklch(0.65 0.15 230)',
            '--chart-1': 'oklch(0.65 0.15 230)',
            '--chart-2': 'oklch(0.60 0.18 200)',
            '--chart-3': 'oklch(0.70 0.12 180)',
            '--chart-4': 'oklch(0.55 0.10 260)',
            '--chart-5': 'oklch(0.75 0.08 220)',
            '--sidebar': 'oklch(0.18 0.02 240)',
            '--sidebar-foreground': 'oklch(0.92 0.015 225)',
            '--sidebar-primary': 'oklch(0.65 0.15 230)',
            '--sidebar-primary-foreground': 'oklch(0.15 0.02 240)',
            '--sidebar-accent': 'oklch(0.35 0.04 230)',
            '--sidebar-accent-foreground': 'oklch(0.92 0.015 225)',
            '--sidebar-border': 'oklch(0.32 0.025 235)',
            '--sidebar-ring': 'oklch(0.65 0.15 230)',
            '--shadow-color': 'hsl(220 30% 5%)',
        }
    },

    // -------------------------------------------------------------------------
    // FOREST - Deep green tones, nature-inspired
    // -------------------------------------------------------------------------
    forest: {
        name: 'Forest',
        description: 'Deep green tones inspired by nature',
        isDark: false,
        colors: {
            '--background': 'oklch(0.96 0.015 145)',
            '--foreground': 'oklch(0.25 0.04 150)',
            '--card': 'oklch(0.98 0.01 145)',
            '--card-foreground': 'oklch(0.25 0.04 150)',
            '--popover': 'oklch(0.98 0.01 145)',
            '--popover-foreground': 'oklch(0.25 0.04 150)',
            '--primary': 'oklch(0.50 0.12 155)',
            '--primary-foreground': 'oklch(1.0 0 0)',
            '--secondary': 'oklch(0.88 0.04 140)',
            '--secondary-foreground': 'oklch(0.30 0.05 150)',
            '--muted': 'oklch(0.92 0.02 145)',
            '--muted-foreground': 'oklch(0.45 0.04 150)',
            '--accent': 'oklch(0.82 0.06 135)',
            '--accent-foreground': 'oklch(0.25 0.04 150)',
            '--destructive': 'oklch(0.55 0.18 30)',
            '--destructive-foreground': 'oklch(1.0 0 0)',
            '--border': 'oklch(0.85 0.035 142)',
            '--input': 'oklch(0.85 0.035 142)',
            '--ring': 'oklch(0.50 0.12 155)',
            '--chart-1': 'oklch(0.50 0.12 155)',
            '--chart-2': 'oklch(0.55 0.15 130)',
            '--chart-3': 'oklch(0.45 0.10 170)',
            '--chart-4': 'oklch(0.60 0.08 145)',
            '--chart-5': 'oklch(0.40 0.14 160)',
            '--sidebar': 'oklch(0.92 0.02 145)',
            '--sidebar-foreground': 'oklch(0.25 0.04 150)',
            '--sidebar-primary': 'oklch(0.50 0.12 155)',
            '--sidebar-primary-foreground': 'oklch(1.0 0 0)',
            '--sidebar-accent': 'oklch(0.82 0.06 135)',
            '--sidebar-accent-foreground': 'oklch(0.25 0.04 150)',
            '--sidebar-border': 'oklch(0.85 0.035 142)',
            '--sidebar-ring': 'oklch(0.50 0.12 155)',
            '--shadow-color': 'hsl(145 25% 18%)',
        },
        darkColors: {
            '--background': 'oklch(0.18 0.03 150)',
            '--foreground': 'oklch(0.90 0.02 145)',
            '--card': 'oklch(0.22 0.035 148)',
            '--card-foreground': 'oklch(0.90 0.02 145)',
            '--popover': 'oklch(0.22 0.035 148)',
            '--popover-foreground': 'oklch(0.90 0.02 145)',
            '--primary': 'oklch(0.60 0.12 150)',
            '--primary-foreground': 'oklch(0.15 0.03 150)',
            '--secondary': 'oklch(0.28 0.04 148)',
            '--secondary-foreground': 'oklch(0.90 0.02 145)',
            '--muted': 'oklch(0.23 0.025 148)',
            '--muted-foreground': 'oklch(0.72 0.03 145)',
            '--accent': 'oklch(0.32 0.05 145)',
            '--accent-foreground': 'oklch(0.90 0.02 145)',
            '--destructive': 'oklch(0.55 0.18 30)',
            '--destructive-foreground': 'oklch(1.0 0 0)',
            '--border': 'oklch(0.30 0.035 148)',
            '--input': 'oklch(0.30 0.035 148)',
            '--ring': 'oklch(0.60 0.12 150)',
            '--chart-1': 'oklch(0.60 0.12 150)',
            '--chart-2': 'oklch(0.65 0.15 130)',
            '--chart-3': 'oklch(0.55 0.10 170)',
            '--chart-4': 'oklch(0.70 0.08 145)',
            '--chart-5': 'oklch(0.50 0.14 160)',
            '--sidebar': 'oklch(0.16 0.03 150)',
            '--sidebar-foreground': 'oklch(0.90 0.02 145)',
            '--sidebar-primary': 'oklch(0.60 0.12 150)',
            '--sidebar-primary-foreground': 'oklch(0.15 0.03 150)',
            '--sidebar-accent': 'oklch(0.32 0.05 145)',
            '--sidebar-accent-foreground': 'oklch(0.90 0.02 145)',
            '--sidebar-border': 'oklch(0.30 0.035 148)',
            '--sidebar-ring': 'oklch(0.60 0.12 150)',
            '--shadow-color': 'hsl(145 25% 5%)',
        }
    },



    // -------------------------------------------------------------------------
    // MIDNIGHT - Dark purple/indigo, sleek and modern
    // -------------------------------------------------------------------------
    midnight: {
        name: 'Midnight',
        description: 'Sleek dark purple and indigo tones',
        isDark: false,
        colors: {
            '--background': 'oklch(0.95 0.015 280)',
            '--foreground': 'oklch(0.25 0.04 270)',
            '--card': 'oklch(0.98 0.01 280)',
            '--card-foreground': 'oklch(0.25 0.04 270)',
            '--popover': 'oklch(0.98 0.01 280)',
            '--popover-foreground': 'oklch(0.25 0.04 270)',
            '--primary': 'oklch(0.50 0.18 280)',
            '--primary-foreground': 'oklch(1.0 0 0)',
            '--secondary': 'oklch(0.88 0.04 275)',
            '--secondary-foreground': 'oklch(0.30 0.05 270)',
            '--muted': 'oklch(0.92 0.02 278)',
            '--muted-foreground': 'oklch(0.48 0.05 275)',
            '--accent': 'oklch(0.82 0.07 290)',
            '--accent-foreground': 'oklch(0.25 0.04 270)',
            '--destructive': 'oklch(0.55 0.20 25)',
            '--destructive-foreground': 'oklch(1.0 0 0)',
            '--border': 'oklch(0.86 0.04 278)',
            '--input': 'oklch(0.86 0.04 278)',
            '--ring': 'oklch(0.50 0.18 280)',
            '--chart-1': 'oklch(0.55 0.20 280)',
            '--chart-2': 'oklch(0.50 0.22 310)',
            '--chart-3': 'oklch(0.60 0.15 250)',
            '--chart-4': 'oklch(0.45 0.18 290)',
            '--chart-5': 'oklch(0.65 0.12 270)',
            '--sidebar': 'oklch(0.92 0.02 278)',
            '--sidebar-foreground': 'oklch(0.25 0.04 270)',
            '--sidebar-primary': 'oklch(0.50 0.18 280)',
            '--sidebar-primary-foreground': 'oklch(1.0 0 0)',
            '--sidebar-accent': 'oklch(0.82 0.07 290)',
            '--sidebar-accent-foreground': 'oklch(0.25 0.04 270)',
            '--sidebar-border': 'oklch(0.86 0.04 278)',
            '--sidebar-ring': 'oklch(0.50 0.18 280)',
            '--shadow-color': 'hsl(270 30% 15%)',
        },
        darkColors: {
            '--background': 'oklch(0.15 0.04 280)',
            '--foreground': 'oklch(0.92 0.02 278)',
            '--card': 'oklch(0.20 0.05 278)',
            '--card-foreground': 'oklch(0.92 0.02 278)',
            '--popover': 'oklch(0.20 0.05 278)',
            '--popover-foreground': 'oklch(0.92 0.02 278)',
            '--primary': 'oklch(0.65 0.18 275)',
            '--primary-foreground': 'oklch(0.12 0.04 280)',
            '--secondary': 'oklch(0.25 0.05 278)',
            '--secondary-foreground': 'oklch(0.92 0.02 278)',
            '--muted': 'oklch(0.20 0.035 278)',
            '--muted-foreground': 'oklch(0.72 0.03 278)',
            '--accent': 'oklch(0.30 0.07 285)',
            '--accent-foreground': 'oklch(0.92 0.02 278)',
            '--destructive': 'oklch(0.55 0.20 25)',
            '--destructive-foreground': 'oklch(1.0 0 0)',
            '--border': 'oklch(0.28 0.05 278)',
            '--input': 'oklch(0.28 0.05 278)',
            '--ring': 'oklch(0.65 0.18 275)',
            '--chart-1': 'oklch(0.65 0.20 275)',
            '--chart-2': 'oklch(0.60 0.22 310)',
            '--chart-3': 'oklch(0.70 0.15 250)',
            '--chart-4': 'oklch(0.55 0.18 290)',
            '--chart-5': 'oklch(0.75 0.12 270)',
            '--sidebar': 'oklch(0.13 0.04 280)',
            '--sidebar-foreground': 'oklch(0.92 0.02 278)',
            '--sidebar-primary': 'oklch(0.65 0.18 275)',
            '--sidebar-primary-foreground': 'oklch(0.12 0.04 280)',
            '--sidebar-accent': 'oklch(0.30 0.07 285)',
            '--sidebar-accent-foreground': 'oklch(0.92 0.02 278)',
            '--sidebar-border': 'oklch(0.28 0.05 278)',
            '--sidebar-ring': 'oklch(0.65 0.18 275)',
            '--shadow-color': 'hsl(270 30% 3%)',
        }
    },

    // -------------------------------------------------------------------------
    // BLOSSOM - Soft pink and lavender, romantic and feminine
    // -------------------------------------------------------------------------
    blossom: {
        name: 'Blossom',
        description: 'Soft pink and lavender with romantic vibes',
        isDark: false,
        colors: {
            // Light blush background with warm undertones
            '--background': 'oklch(0.97 0.02 350)',
            '--foreground': 'oklch(0.30 0.05 340)',
            // Soft rose cards
            '--card': 'oklch(0.99 0.015 355)',
            '--card-foreground': 'oklch(0.30 0.05 340)',
            '--popover': 'oklch(0.99 0.015 355)',
            '--popover-foreground': 'oklch(0.30 0.05 340)',
            // Rose pink primary - vibrant but not harsh
            '--primary': 'oklch(0.65 0.18 350)',
            '--primary-foreground': 'oklch(1.0 0 0)',
            // Lavender secondary tones
            '--secondary': 'oklch(0.90 0.04 310)',
            '--secondary-foreground': 'oklch(0.35 0.06 340)',
            // Soft muted pink
            '--muted': 'oklch(0.94 0.025 345)',
            '--muted-foreground': 'oklch(0.50 0.05 340)',
            // Rose gold accent
            '--accent': 'oklch(0.85 0.05 350)',
            '--accent-foreground': 'oklch(0.30 0.05 340)',
            '--destructive': 'oklch(0.55 0.20 25)',
            '--destructive-foreground': 'oklch(1.0 0 0)',
            // Soft pink borders
            '--border': 'oklch(0.88 0.04 345)',
            '--input': 'oklch(0.88 0.04 345)',
            '--ring': 'oklch(0.65 0.18 350)',
            // Harmonious chart colors in pinks, magentas, lavenders
            '--chart-1': 'oklch(0.65 0.20 350)',  // Rose pink
            '--chart-2': 'oklch(0.55 0.22 320)',  // Magenta
            '--chart-3': 'oklch(0.70 0.15 300)',  // Lavender
            '--chart-4': 'oklch(0.60 0.18 10)',   // Coral
            '--chart-5': 'oklch(0.75 0.12 330)',  // Soft orchid
            // Sidebar with subtle blush tint
            '--sidebar': 'oklch(0.94 0.025 345)',
            '--sidebar-foreground': 'oklch(0.30 0.05 340)',
            '--sidebar-primary': 'oklch(0.65 0.18 350)',
            '--sidebar-primary-foreground': 'oklch(1.0 0 0)',
            '--sidebar-accent': 'oklch(0.85 0.05 350)',
            '--sidebar-accent-foreground': 'oklch(0.30 0.05 340)',
            '--sidebar-border': 'oklch(0.88 0.04 345)',
            '--sidebar-ring': 'oklch(0.65 0.18 350)',
            '--shadow-color': 'hsl(340 30% 25%)',
        },
        darkColors: {
            // Deep rose-tinted purple background
            '--background': 'oklch(0.18 0.04 330)',
            '--foreground': 'oklch(0.92 0.025 345)',
            // Rich plum cards
            '--card': 'oklch(0.23 0.05 335)',
            '--card-foreground': 'oklch(0.92 0.025 345)',
            '--popover': 'oklch(0.23 0.05 335)',
            '--popover-foreground': 'oklch(0.92 0.025 345)',
            // Bright pink primary
            '--primary': 'oklch(0.72 0.18 350)',
            '--primary-foreground': 'oklch(0.15 0.04 330)',
            // Muted purple secondary
            '--secondary': 'oklch(0.28 0.05 325)',
            '--secondary-foreground': 'oklch(0.92 0.025 345)',
            '--muted': 'oklch(0.22 0.035 330)',
            '--muted-foreground': 'oklch(0.75 0.03 345)',
            // Dusty rose accent
            '--accent': 'oklch(0.38 0.07 355)',
            '--accent-foreground': 'oklch(0.92 0.025 345)',
            '--destructive': 'oklch(0.55 0.20 25)',
            '--destructive-foreground': 'oklch(1.0 0 0)',
            '--border': 'oklch(0.30 0.05 330)',
            '--input': 'oklch(0.30 0.05 330)',
            '--ring': 'oklch(0.72 0.18 350)',
            // Vibrant chart colors for dark mode
            '--chart-1': 'oklch(0.72 0.20 350)',  // Bright rose
            '--chart-2': 'oklch(0.65 0.22 320)',  // Hot pink
            '--chart-3': 'oklch(0.78 0.15 300)',  // Light lavender
            '--chart-4': 'oklch(0.68 0.18 10)',   // Peachy coral
            '--chart-5': 'oklch(0.80 0.12 330)',  // Soft pink
            // Dark sidebar with rose undertones
            '--sidebar': 'oklch(0.16 0.04 330)',
            '--sidebar-foreground': 'oklch(0.92 0.025 345)',
            '--sidebar-primary': 'oklch(0.72 0.18 350)',
            '--sidebar-primary-foreground': 'oklch(0.15 0.04 330)',
            '--sidebar-accent': 'oklch(0.38 0.07 355)',
            '--sidebar-accent-foreground': 'oklch(0.92 0.025 345)',
            '--sidebar-border': 'oklch(0.30 0.05 330)',
            '--sidebar-ring': 'oklch(0.72 0.18 350)',
            '--shadow-color': 'hsl(330 30% 5%)',
        }
    },

    // -------------------------------------------------------------------------
    // SNOW - Crisp white and ice blue tones
    // -------------------------------------------------------------------------
    snow: {
        name: 'Snow',
        description: 'Crisp white and minimal ice blue tones',
        isDark: false,
        colors: {
            // Near pure white background
            '--background': 'oklch(0.99 0.002 220)',
            '--foreground': 'oklch(0.25 0.02 220)',
            // Pure white cards
            '--card': 'oklch(1.00 0 0)',
            '--card-foreground': 'oklch(0.25 0.02 220)',
            '--popover': 'oklch(1.00 0 0)',
            '--popover-foreground': 'oklch(0.25 0.02 220)',
            // Minimalist primary - very muted cool gray/blue
            '--primary': 'oklch(0.55 0.04 220)',
            '--primary-foreground': 'oklch(1.00 0 0)',
            // Extremely light secondary
            '--secondary': 'oklch(0.97 0.005 220)',
            '--secondary-foreground': 'oklch(0.35 0.02 220)',
            // Frosty muted tones
            '--muted': 'oklch(0.98 0.003 220)',
            '--muted-foreground': 'oklch(0.50 0.02 220)',
            // Very pale ice accent
            '--accent': 'oklch(0.95 0.01 220)',
            '--accent-foreground': 'oklch(0.25 0.02 220)',
            '--destructive': 'oklch(0.55 0.18 20)',
            '--destructive-foreground': 'oklch(1.00 0 0)',
            // Subtle frosty borders
            '--border': 'oklch(0.93 0.005 220)',
            '--input': 'oklch(0.93 0.005 220)',
            '--ring': 'oklch(0.55 0.04 220)',
            // High-key chart colors
            '--chart-1': 'oklch(0.55 0.05 220)',  // Muted blue
            '--chart-2': 'oklch(0.65 0.04 200)',  // Muted teal
            '--chart-3': 'oklch(0.75 0.03 240)',  // Muted indigo
            '--chart-4': 'oklch(0.50 0.02 220)',  // Steel gray
            '--chart-5': 'oklch(0.85 0.02 220)',  // Pale frost
            // Sidebar - very clean and white
            '--sidebar': 'oklch(0.985 0.002 220)',
            '--sidebar-foreground': 'oklch(0.25 0.02 220)',
            '--sidebar-primary': 'oklch(0.55 0.04 220)',
            '--sidebar-primary-foreground': 'oklch(1.00 0 0)',
            '--sidebar-accent': 'oklch(0.95 0.01 220)',
            '--sidebar-accent-foreground': 'oklch(0.25 0.02 220)',
            '--sidebar-border': 'oklch(0.93 0.005 220)',
            '--sidebar-ring': 'oklch(0.55 0.04 220)',
            '--shadow-color': 'hsl(220 10% 20%)',
        },
        darkColors: {
            '--background': 'oklch(0.30 0.01 220)',
            '--foreground': 'oklch(0.98 0.005 220)',
            '--card': 'oklch(0.35 0.015 220)',
            '--card-foreground': 'oklch(0.98 0.005 220)',
            '--popover': 'oklch(0.35 0.015 220)',
            '--popover-foreground': 'oklch(0.98 0.005 220)',
            '--primary': 'oklch(0.70 0.04 220)',
            '--primary-foreground': 'oklch(0.20 0.01 220)',
            '--secondary': 'oklch(0.40 0.02 220)',
            '--secondary-foreground': 'oklch(0.98 0.005 220)',
            '--muted': 'oklch(0.32 0.01 220)',
            '--muted-foreground': 'oklch(0.80 0.01 220)',
            '--accent': 'oklch(0.45 0.03 220)',
            '--accent-foreground': 'oklch(0.98 0.005 220)',
            '--destructive': 'oklch(0.55 0.18 20)',
            '--destructive-foreground': 'oklch(1.00 0 0)',
            '--border': 'oklch(0.38 0.015 220)',
            '--input': 'oklch(0.38 0.015 220)',
            '--ring': 'oklch(0.70 0.01 220)',
            '--chart-1': 'oklch(0.70 0.04 220)',
            '--chart-2': 'oklch(0.65 0.03 200)',
            '--chart-3': 'oklch(0.75 0.02 240)',
            '--chart-4': 'oklch(0.60 0.01 220)',
            '--chart-5': 'oklch(0.85 0.01 220)',
            '--sidebar': 'oklch(0.28 0.01 220)',
            '--sidebar-foreground': 'oklch(0.98 0.005 220)',
            '--sidebar-primary': 'oklch(0.70 0.04 220)',
            '--sidebar-primary-foreground': 'oklch(0.20 0.01 220)',
            '--sidebar-accent': 'oklch(0.45 0.03 220)',
            '--sidebar-accent-foreground': 'oklch(0.98 0.005 220)',
            '--sidebar-border': 'oklch(0.38 0.015 220)',
            '--sidebar-ring': 'oklch(0.70 0.04 220)',
            '--shadow-color': 'hsl(220 10% 5%)',
        }
    },



    // -------------------------------------------------------------------------
    // FRESH ORANGE - Vibrant citrus orange tones
    // -------------------------------------------------------------------------
    freshOrange: {
        name: 'Fresh Orange',
        description: 'Vibrant citrus orange with warm energy',
        isDark: false,
        colors: {
            // Warm cream background
            '--background': 'oklch(0.97 0.02 70)',
            '--foreground': 'oklch(0.25 0.04 50)',
            // Light peach cards
            '--card': 'oklch(0.99 0.015 65)',
            '--card-foreground': 'oklch(0.25 0.04 50)',
            '--popover': 'oklch(0.99 0.015 65)',
            '--popover-foreground': 'oklch(0.25 0.04 50)',
            // Vibrant orange primary
            '--primary': 'oklch(0.65 0.20 45)',
            '--primary-foreground': 'oklch(1.0 0 0)',
            // Soft peach secondary
            '--secondary': 'oklch(0.92 0.04 60)',
            '--secondary-foreground': 'oklch(0.35 0.05 50)',
            // Muted warm tones
            '--muted': 'oklch(0.94 0.025 65)',
            '--muted-foreground': 'oklch(0.50 0.04 50)',
            // Golden accent
            '--accent': 'oklch(0.85 0.10 75)',
            '--accent-foreground': 'oklch(0.25 0.04 50)',
            '--destructive': 'oklch(0.55 0.22 25)',
            '--destructive-foreground': 'oklch(1.0 0 0)',
            // Soft tangerine borders
            '--border': 'oklch(0.88 0.05 60)',
            '--input': 'oklch(0.88 0.05 60)',
            '--ring': 'oklch(0.65 0.20 45)',
            // Citrus chart colors
            '--chart-1': 'oklch(0.65 0.20 45)',  // Bright orange
            '--chart-2': 'oklch(0.70 0.18 70)',  // Golden yellow
            '--chart-3': 'oklch(0.55 0.15 30)',  // Deep tangerine
            '--chart-4': 'oklch(0.75 0.12 55)',  // Peach
            '--chart-5': 'oklch(0.60 0.22 40)',  // Burnt orange
            // Sidebar with warm peach tint
            '--sidebar': 'oklch(0.94 0.03 65)',
            '--sidebar-foreground': 'oklch(0.25 0.04 50)',
            '--sidebar-primary': 'oklch(0.65 0.20 45)',
            '--sidebar-primary-foreground': 'oklch(1.0 0 0)',
            '--sidebar-accent': 'oklch(0.85 0.10 75)',
            '--sidebar-accent-foreground': 'oklch(0.25 0.04 50)',
            '--sidebar-border': 'oklch(0.88 0.05 60)',
            '--sidebar-ring': 'oklch(0.65 0.20 45)',
            '--shadow-color': 'hsl(35 40% 20%)',
        },
        darkColors: {
            // Deep burnt orange background
            '--background': 'oklch(0.18 0.04 40)',
            '--foreground': 'oklch(0.95 0.025 60)',
            // Rich amber cards
            '--card': 'oklch(0.23 0.05 42)',
            '--card-foreground': 'oklch(0.95 0.025 60)',
            '--popover': 'oklch(0.23 0.05 42)',
            '--popover-foreground': 'oklch(0.95 0.025 60)',
            // Bright orange primary
            '--primary': 'oklch(0.72 0.18 50)',
            '--primary-foreground': 'oklch(0.15 0.04 40)',
            // Muted terracotta secondary
            '--secondary': 'oklch(0.28 0.05 42)',
            '--secondary-foreground': 'oklch(0.95 0.025 60)',
            '--muted': 'oklch(0.22 0.035 42)',
            '--muted-foreground': 'oklch(0.78 0.03 60)',
            // Warm amber accent
            '--accent': 'oklch(0.38 0.08 55)',
            '--accent-foreground': 'oklch(0.95 0.025 60)',
            '--destructive': 'oklch(0.55 0.22 25)',
            '--destructive-foreground': 'oklch(1.0 0 0)',
            '--border': 'oklch(0.30 0.05 42)',
            '--input': 'oklch(0.30 0.05 42)',
            '--ring': 'oklch(0.72 0.18 50)',
            // Vibrant dark mode chart colors
            '--chart-1': 'oklch(0.72 0.20 50)',  // Bright orange
            '--chart-2': 'oklch(0.78 0.18 70)',  // Golden
            '--chart-3': 'oklch(0.62 0.15 35)',  // Deep rust
            '--chart-4': 'oklch(0.82 0.12 60)',  // Light peach
            '--chart-5': 'oklch(0.68 0.22 45)',  // Vibrant tangerine
            // Dark sidebar with warm undertones
            '--sidebar': 'oklch(0.16 0.04 40)',
            '--sidebar-foreground': 'oklch(0.95 0.025 60)',
            '--sidebar-primary': 'oklch(0.72 0.18 50)',
            '--sidebar-primary-foreground': 'oklch(0.15 0.04 40)',
            '--sidebar-accent': 'oklch(0.38 0.08 55)',
            '--sidebar-accent-foreground': 'oklch(0.95 0.025 60)',
            '--sidebar-border': 'oklch(0.30 0.05 42)',
            '--sidebar-ring': 'oklch(0.72 0.18 50)',
            '--shadow-color': 'hsl(35 40% 5%)',
        }
    }

};

// ============================================================================
// STORAGE KEYS
// ============================================================================
const THEME_STORAGE_KEY = 'dcaclab-theme';
const DARK_MODE_STORAGE_KEY = 'dcaclab-dark-mode';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get the current theme name from localStorage
 * @returns {string} Theme name
 */
export const getCurrentTheme = () => {
    if (typeof localStorage === 'undefined') return 'slate';
    return localStorage.getItem(THEME_STORAGE_KEY) || 'slate';
};

/**
 * Check if dark mode is enabled
 * @returns {boolean} Always returns false as dark mode is disabled
 */
export const isDarkMode = () => {
    return false;
};

/**
 * Get all available theme names
 * @returns {string[]}
 */
export const getThemeNames = () => Object.keys(themes);

/**
 * Get theme metadata (name, description, isDark)
 * @param {string} themeName
 * @returns {Object|null}
 */
export const getThemeInfo = (themeName) => {
    const theme = themes[themeName];
    if (!theme) return null;
    return {
        id: themeName,
        name: theme.name,
        description: theme.description,
        isDark: theme.isDark
    };
};

/**
 * Get all themes with their metadata
 * @returns {Object[]}
 */
export const getAllThemes = () => {
    return Object.keys(themes).map(key => ({
        id: key,
        ...getThemeInfo(key)
    }));
};

// ============================================================================
// MAIN THEME APPLICATION FUNCTION
// ============================================================================

/**
 * Convert a CSS color string to hex integer for Haxe
 * Works with OKLCH, rgb, hex, hsl, and all CSS color formats
 * Uses canvas 2D context which normalizes all colors to RGB
 * @param {string} colorStr - CSS color string
 * @returns {number} Hex integer (e.g., 0x3d3530)
 */
const cssColorToHexInt = (colorStr) => {
    try {
        // Use a canvas which normalizes all color formats to RGB
        const canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = 1;
        const ctx = canvas.getContext('2d');

        // The fillStyle property normalizes any CSS color to rgb() or rgba()
        ctx.fillStyle = colorStr;
        ctx.fillRect(0, 0, 1, 1);

        // Get the pixel data - this is always in RGBA format
        const imageData = ctx.getImageData(0, 0, 1, 1);
        const [r, g, b] = imageData.data;

        return (r << 16) | (g << 8) | b;
    } catch (e) {
        console.warn('cssColorToHexInt failed for:', colorStr, e);
        return 0x3d3530; // Fallback to default earthy color
    }
};

const labBridge = () =>
    (typeof window !== 'undefined' && window.com?.clientside?.main?.Html5Bootstrap?.jsCall)
        ? window.com.clientside.main.Html5Bootstrap
        : null;

/**
 * Send the palette across the JS -> Haxe bridge.
 * @returns {boolean} true if the bridge existed and the call was made
 */
const sendThemeToLab = (bridge, themeName, useDark, colorSet) => {
    bridge.jsCall('setTheme', {
        themeName: themeName,
        isDark: useDark,
        // Panel colour always comes from --sidebar. Espresso used to be forced to a
        // light 0xE8DCD1 here, which left its light --sidebar-foreground painting text
        // on a near-identical background: 1.01 contrast, i.e. invisible. Every palette
        // pairs --sidebar-foreground against --sidebar, so deriving both from the same
        // pair keeps the lab chrome legible in all nine themes (espresso 14.87).
        panelColor: cssColorToHexInt(colorSet['--sidebar']),
        topMenuBackgroundColor: cssColorToHexInt(colorSet['--sidebar-border']),
        panelForeground: cssColorToHexInt(colorSet['--sidebar-foreground']),
        panelGBColor: cssColorToHexInt(colorSet['--accent']),
        primaryColor: cssColorToHexInt(colorSet['--primary']),
        propertyRowColor: cssColorToHexInt(colorSet['--chart-4'])
    }, null);
    return true;
};

let pendingLabDelivery = null;

/**
 * Deliver the palette to the lab, waiting for the canvas if it has not loaded yet.
 *
 * The real lab bundle is fetched asynchronously by lime.embed(), so on a cold page
 * load `window.com.clientside.main.Html5Bootstrap` does not exist yet when React
 * mounts and initializeTheme() runs - measured at ~8.5s before it appears. The call
 * used to be dropped with only a console warning and never retried, so a saved theme
 * was silently lost on every refresh and the canvas kept its compiled-in defaults
 * until the user picked a theme again. Retry until the bridge shows up.
 *
 * Sending twice is harmless: applyTheme is idempotent (verified pixel-identical over
 * repeated A->B->A switches), and the Haxe side defers an early call via
 * pendingThemeRefresh until markInitialized() runs.
 */
const deliverThemeToLab = (themeName, useDark, colorSet) => {
    if (typeof window === 'undefined') return;

    // a newer theme supersedes any delivery still waiting on the canvas
    if (pendingLabDelivery) {
        clearInterval(pendingLabDelivery);
        pendingLabDelivery = null;
    }

    const bridge = labBridge();
    if (bridge) {
        sendThemeToLab(bridge, themeName, useDark, colorSet);
        return;
    }

    const startedAt = Date.now();
    const TIMEOUT_MS = 60000;
    pendingLabDelivery = setInterval(() => {
        const ready = labBridge();
        if (ready) {
            clearInterval(pendingLabDelivery);
            pendingLabDelivery = null;
            sendThemeToLab(ready, themeName, useDark, colorSet);
        } else if (Date.now() - startedAt > TIMEOUT_MS) {
            clearInterval(pendingLabDelivery);
            pendingLabDelivery = null;
            console.warn('Lab canvas never became available; theme applied to the page only.');
        }
    }, 250);
};

/**
 * Apply a theme by name
 * @param {string} themeName - Name of the theme to apply
 * @param {boolean|null} darkMode - Force dark/light mode, or null to use stored preference
 * @returns {boolean} Success status
 */
export const applyTheme = (themeName, darkMode = null, isInitialization = false) => {
    const theme = themes[themeName];
    if (!theme) {
        console.warn(`Theme "${themeName}" not found. Using "slate" instead.`);
        return applyTheme('slate', darkMode, isInitialization);
    }

    // Read before the persist further down overwrites it, so the analytics event can
    // report what the user switched away from and not just where they landed.
    const previousTheme = getCurrentTheme();

    const root = document.documentElement;

    // Determine if we should use dark mode - Always false now
    const useDark = false;
    const colorSet = theme.colors;

    // Apply all CSS variables to :root
    Object.entries(colorSet).forEach(([property, value]) => {
        root.style.setProperty(property, value);
    });

    // Apply common (non-color) theme properties
    root.style.setProperty('--radius', '0.5rem');
    root.style.setProperty('--spacing', '0.25rem');
    root.style.setProperty('--tracking-normal', '0em');

    // Shadow variables
    const shadowColor = colorSet['--shadow-color'] || 'hsl(0 0% 20%)';
    root.style.setProperty('--shadow-2xs', `2px 3px 5px 0px ${shadowColor.replace(')', ' / 0.06)')}`);
    root.style.setProperty('--shadow-xs', `2px 3px 5px 0px ${shadowColor.replace(')', ' / 0.06)')}`);
    root.style.setProperty('--shadow-sm', `2px 3px 5px 0px ${shadowColor.replace(')', ' / 0.12)')}, 2px 1px 2px -1px ${shadowColor.replace(')', ' / 0.12)')}`);
    root.style.setProperty('--shadow', `2px 3px 5px 0px ${shadowColor.replace(')', ' / 0.12)')}, 2px 1px 2px -1px ${shadowColor.replace(')', ' / 0.12)')}`);
    root.style.setProperty('--shadow-md', `2px 3px 5px 0px ${shadowColor.replace(')', ' / 0.12)')}, 2px 2px 4px -1px ${shadowColor.replace(')', ' / 0.12)')}`);
    root.style.setProperty('--shadow-lg', `2px 3px 5px 0px ${shadowColor.replace(')', ' / 0.12)')}, 2px 4px 6px -1px ${shadowColor.replace(')', ' / 0.12)')}`);
    root.style.setProperty('--shadow-xl', `2px 3px 5px 0px ${shadowColor.replace(')', ' / 0.12)')}, 2px 8px 10px -1px ${shadowColor.replace(')', ' / 0.12)')}`);
    root.style.setProperty('--shadow-2xl', `2px 3px 5px 0px ${shadowColor.replace(')', ' / 0.30)')}`);

    // Update Toastify overrides
    root.style.setProperty('--toastify-color-light', 'var(--card)');
    root.style.setProperty('--toastify-text-color-light', 'var(--card-foreground)');
    root.style.setProperty('--toastify-color-info', 'var(--card)');
    root.style.setProperty('--toastify-color-success', 'var(--card)');
    root.style.setProperty('--toastify-color-warning', 'var(--card)');
    root.style.setProperty('--toastify-color-error', 'var(--card)');

    // Toggle dark class on body/html for any additional CSS targeting
    if (useDark) {
        root.classList.add('dark');
        root.setAttribute('data-theme', 'dark');
    } else {
        root.classList.remove('dark');
        root.setAttribute('data-theme', 'light');
    }

    // Persist selections
    if (typeof localStorage !== 'undefined') {
        localStorage.setItem(THEME_STORAGE_KEY, themeName);
    }

    // Pass theme colors to the Haxe canvas. Built from the in-memory theme object,
    // not from applied CSS, so there is nothing to wait for before sending.
    deliverThemeToLab(themeName, useDark, colorSet);

    // Dispatch custom event for components that need to react to theme changes
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('themechange', {
            detail: { theme: themeName, dark: useDark }
        }));

        // Google Analytics - Track theme preference. Skipped on initialization so a
        // page load is not counted as a deliberate switch; previous_theme shows what
        // the user moved away from, which distinguishes settling on a theme from
        // cycling through them.
        if (typeof window.gtag === 'function' && !isInitialization) {
            window.gtag('event', 'theme_applied', {
                event_category: 'Theme',
                event_label: themeName,
                previous_theme: previousTheme
            });
        }
    }

    console.log(`🎨 Theme applied: ${theme.name} (${useDark ? 'dark' : 'light'} mode)`);
    return true;
};

/**
 * Toggle between light and dark mode - Disabled
 * @returns {boolean} Always returns false
 */
export const toggleDarkMode = () => {
    return false;
};

/**
 * Initialize theme on page load
 * Call this in your app's entry point
 */
export const initializeTheme = () => {
    const theme = getCurrentTheme();
    const dark = isDarkMode();
    applyTheme(theme, dark, true);
};

// ============================================================================
// CONVENIENCE EXPORTS
// ============================================================================
export { themes };
export default {
    applyTheme,
    toggleDarkMode,
    initializeTheme,
    getCurrentTheme,
    isDarkMode,
    getThemeNames,
    getThemeInfo,
    getAllThemes,
    themes
};
